/**
 * Route-role matrix — single source of truth (unit-tested in
 * routeAccess.test.ts). Kept dependency-free so it can be imported by guards
 * and tests without pulling in the Supabase client.
 *
 * This app's roles are ADDITIVE, not exclusive: `is_helper` is a one-way
 * capability flag and a helper may also act as a client (the "Switch to
 * Client" flow). So:
 *   - 'helper' routes require helper capability (is_helper === true).
 *   - 'client' routes require only authentication — ANY signed-in user
 *     (client OR helper) may use client surfaces. `!is_helper` would wrongly
 *     lock helpers out of their own client dashboard.
 *   - 'admin' routes match profiles.role === 'admin' here, but the real admin
 *     gate is ProtectedAdminRoute + the user_roles table; prefer that.
 */
export type UserType = 'client' | 'helper' | 'admin';

/**
 * ── Route registry (issue #329) ──────────────────────────────────────────────
 * Single typed source of truth for every top-level route's access metadata.
 * Previously this lived in two places that silently drifted: the `<Route>` table
 * in src/App.tsx and a separate `PUBLIC_ROUTES` array in AuthWrapper.tsx — the
 * drift is how dev test routes ended up both routed and public (#325).
 *
 * AuthWrapper now derives its public / auth-exempt checks from this registry
 * (see `isPublicPath` / `AUTH_EXEMPT_PATHS`), and routeRegistry.test.ts asserts
 * every route in App.tsx is registered here with an explicit `public` + `access`
 * (no silent defaults) and that the derived public check is behaviourally
 * identical to the historical list.
 *
 * The initial refactor encoded CURRENT behaviour verbatim (a no-op), recording
 * genuine public/role bugs found during the inventory as `driftBug` entries.
 * Fixed since (#428): /admin/legacy-settings is now ProtectedAdminRoute-guarded;
 * the legal/marketing pages + redirect-to-public aliases are public; static
 * guarded routes (/helper/bookings, /helper/my-services) are no longer treated
 * as public via the /helper/:helperId prefix (exact match wins, router-style);
 * env:'dev' sandbox routes are only public in dev builds; the dead /pricing
 * entry is removed. All deliberate deltas vs the legacy AuthWrapper lists are
 * asserted explicitly in routeRegistry.test.ts; remaining driftBug entries are
 * still open in #428.
 */
export type RouteAccess = 'public' | 'authenticated' | 'helper' | 'admin';
export type RouteEnv = 'all' | 'dev' | 'uat';

export interface RouteMeta {
  /** Exact route path as declared in App.tsx (params kept, e.g. /helper/:helperId). */
  path: string;
  /** True iff unauthenticated users may load it (AuthWrapper does not redirect). */
  public: boolean;
  /** Effective access requirement once loaded. */
  access: RouteAccess;
  /** Build environment the route exists in. */
  env: RouteEnv;
  /** Skips the post-login redirect handling (auth/reset/oauth landing). */
  authExempt?: boolean;
  /** Redirect target, for alias/redirect routes. */
  redirect?: string;
  /** True for registry entries with no `<Route>` in the <Routes> block. */
  noRoute?: boolean;
  /** Records a known public/role drift bug to fix separately (does NOT change behaviour). */
  driftBug?: string;
}

/** Strip the first path parameter to get the static prefix AuthWrapper matches on. */
export function staticPrefix(path: string): string {
  const i = path.indexOf('/:');
  return i === -1 ? path : path.slice(0, i);
}

export const ROUTE_REGISTRY: RouteMeta[] = [
  // ── Public marketing / auth / legal ──
  { path: '/', public: true, access: 'public', env: 'all' },
  { path: '/auth', public: true, access: 'public', env: 'all', authExempt: true },
  { path: '/reset-password', public: true, access: 'public', env: 'all', authExempt: true },
  { path: '/post-auth', public: true, access: 'public', env: 'all', authExempt: true },
  // WhatsApp linking (milestone 1): must render signed out, so the page can
  // park the #t= token before any redirect drops the fragment. The database
  // functions it calls require a signed-in, freshly authenticated customer.
  { path: '/link/whatsapp', public: true, access: 'public', env: 'all' },
  { path: '/login', public: true, access: 'public', env: 'all' },
  { path: '/signup', public: true, access: 'public', env: 'all' },
  { path: '/find-services', public: true, access: 'public', env: 'all', redirect: '/booking-flow' },
  { path: '/helper/:helperId', public: true, access: 'public', env: 'all' },
  { path: '/services', public: true, access: 'public', env: 'all' },
  { path: '/services/all', public: true, access: 'public', env: 'all' },
  { path: '/services/:category', public: true, access: 'public', env: 'all' },
  { path: '/become-helper', public: true, access: 'public', env: 'all' },
  { path: '/how-it-works', public: true, access: 'public', env: 'all' },
  { path: '/childcare', public: true, access: 'public', env: 'all' },
  { path: '/elderly-care', public: true, access: 'public', env: 'all' },
  { path: '/cleaning', public: true, access: 'public', env: 'all' },
  { path: '/tutoring', public: true, access: 'public', env: 'all' },
  { path: '/pet-care', public: true, access: 'public', env: 'all' },
  { path: '/security', public: true, access: 'public', env: 'all' },
  { path: '/maintenance', public: true, access: 'public', env: 'all' },
  { path: '/gardening', public: true, access: 'public', env: 'all' },
  { path: '/about', public: true, access: 'public', env: 'all' },
  { path: '/support', public: true, access: 'public', env: 'all' },
  { path: '/safety', public: true, access: 'public', env: 'all' },
  { path: '/faq', public: true, access: 'public', env: 'all' },
  { path: '/guides', public: true, access: 'public', env: 'all' },
  { path: '/blog', public: true, access: 'public', env: 'all' },
  { path: '/blog/:slug', public: true, access: 'public', env: 'all' },
  { path: '/terms', public: true, access: 'public', env: 'all' },
  { path: '/privacy', public: true, access: 'public', env: 'all' },
  { path: '/careers', public: true, access: 'public', env: 'all' },
  { path: '/helper-resources', public: true, access: 'public', env: 'all' },
  { path: '/cookies', public: true, access: 'public', env: 'all' },
  // The link in every offer Kanfy sends; no sign-in (20270548000000).
  { path: '/unsubscribe', public: true, access: 'public', env: 'all' },
  { path: '/guidelines', public: true, access: 'public', env: 'all' },
  { path: '/contact', public: true, access: 'public', env: 'all' },
  { path: '/admin/login', public: true, access: 'public', env: 'all' },
  { path: '/admin/setup', public: true, access: 'public', env: 'all' },
  // Pseudo-entry: in PUBLIC_ROUTES historically but not a <Route> in <Routes>.
  { path: '/uat', public: true, access: 'public', env: 'uat', noRoute: true, driftBug: 'Served by AppRouter short-circuit before <Routes>, not a <Route> element.' },
  // Dev-only sandbox pages: routed only under import.meta.env.DEV (DCE in prod).
  // env:'dev' also drops them from the public list in production builds (#325/#428).
  { path: '/dev/address-picker-test', public: true, access: 'public', env: 'dev' },

  // ── Authenticated (any signed-in user) ──
  { path: '/logout', public: false, access: 'authenticated', env: 'all' },
  { path: '/profile-creation', public: false, access: 'authenticated', env: 'all' },
  { path: '/helper-profile', public: false, access: 'authenticated', env: 'all' },
  { path: '/client-profile', public: false, access: 'authenticated', env: 'all' },
  { path: '/find-helpers', public: true, access: 'public', env: 'all', redirect: '/booking-flow' },
  { path: '/helper/bookings', public: false, access: 'helper', env: 'all' },
  { path: '/browse-helpers', public: false, access: 'authenticated', env: 'all' },
  { path: '/dashboard', public: false, access: 'authenticated', env: 'all' },
  { path: '/helper-dashboard', public: false, access: 'authenticated', env: 'all', redirect: '/dashboard/helper' },
  { path: '/client-account', public: false, access: 'authenticated', env: 'all' },
  { path: '/client-dashboard', public: false, access: 'authenticated', env: 'all' },
  { path: '/verify-identity', public: false, access: 'authenticated', env: 'all' },
  { path: '/request-service', public: true, access: 'public', env: 'all', redirect: '/booking-flow' },
  // Public by owner decision: a visitor scopes the service, sets the location,
  // fills in the details and picks a helper WITHOUT an account. Sign-in is
  // demanded only at the payment step (BookingFlow gates the step 4 → 5
  // transition) and handleSubmitBooking still re-checks the session before any
  // insert, so opening the route does not open the booking write path.
  { path: '/booking-flow', public: true, access: 'public', env: 'all' },
  { path: '/booking/payment-complete', public: false, access: 'authenticated', env: 'all' },
  // Retired helper-first route, kept as a redirect into /booking-flow
  // (App.tsx → LegacyBookingRedirect). It must match the PUBLIC target it
  // forwards to: AuthWrapper reads this registry before the redirect component
  // renders, so marking it authenticated sent every old bookmark, shared link
  // and indexed URL to a login wall instead of the open flow.
  { path: '/booking/:helperId', public: true, access: 'public', env: 'all', redirect: '/booking-flow' },
  { path: '/my-bookings', public: false, access: 'authenticated', env: 'all' },
  { path: '/my-bookings/:id', public: false, access: 'authenticated', env: 'all', redirect: '/my-bookings' },
  { path: '/my-applications', public: false, access: 'authenticated', env: 'all' },
  { path: '/notifications', public: false, access: 'authenticated', env: 'all' },
  { path: '/my-helpers', public: false, access: 'authenticated', env: 'all' },
  { path: '/favorites', public: false, access: 'authenticated', env: 'all' },
  { path: '/legacy-dashboard', public: false, access: 'authenticated', env: 'all', redirect: '/dashboard' },
    // /onboarding means HELPER onboarding by design (#786) — see App.tsx.
  { path: '/onboarding', public: false, access: 'authenticated', env: 'all', redirect: '/helper-profile' },
  { path: '/helper-onboarding', public: false, access: 'authenticated', env: 'all', redirect: '/helper-profile' },
  { path: '/client-onboarding', public: false, access: 'authenticated', env: 'all', redirect: '/client-profile' },
  { path: '/messages', public: false, access: 'authenticated', env: 'all' },
  { path: '/messages/:conversationId', public: false, access: 'authenticated', env: 'all' },
  { path: '/service-standards', public: true, access: 'public', env: 'all' },
  { path: '/safety-guide', public: true, access: 'public', env: 'all', redirect: '/service-standards' },
  { path: '/jobs', public: false, access: 'authenticated', env: 'all' },
  { path: '/post-job', public: false, access: 'authenticated', env: 'all' },
  { path: '/jobs/:jobId', public: false, access: 'authenticated', env: 'all' },
  { path: '/jobs/:jobId/applicants', public: false, access: 'authenticated', env: 'all' },
  { path: '/cooking', public: true, access: 'public', env: 'all', redirect: '/services/cooking' },
  { path: '/driving', public: true, access: 'public', env: 'all', redirect: '/services/driving' },
  { path: '/plumbing', public: true, access: 'public', env: 'all', redirect: '/services/plumbing' },
  { path: '/platform-rules', public: true, access: 'public', env: 'all' },
  { path: '/data-deletion', public: true, access: 'public', env: 'all' },
  // The deletion email's landing page: the emailed token is the credential,
  // and the account it deletes may be one the visitor is not signed in to.
  { path: '/data-deletion/confirm', public: true, access: 'public', env: 'all' },
  { path: '/search', public: true, access: 'public', env: 'all', redirect: '/booking-flow' },
  { path: '/bookings', public: false, access: 'authenticated', env: 'all', redirect: '/my-bookings' },
  { path: '/helpers', public: true, access: 'public', env: 'all', redirect: '/booking-flow' },
  { path: '/profile', public: false, access: 'authenticated', env: 'all', redirect: '/client-account' },
  // /find-jobs renders its own logged-out experience (sign-in CTAs, RLS returns
  // no jobs for anon); /share-story's testimonials table allows anon INSERT.
  { path: '/find-jobs', public: true, access: 'public', env: 'all' },
  { path: '/share-story', public: true, access: 'public', env: 'all' },
  // An author's consent to publish a hidden legacy story (20270497000000):
  // the RPC checks authorship, so the page needs a session, not a role.
  { path: '/story-consent/:testimonialId', public: false, access: 'authenticated', env: 'all' },

  // ── Helper-only ──
  { path: '/helper/my-services', public: false, access: 'helper', env: 'all' },
  { path: '/dashboard/helper', public: false, access: 'helper', env: 'all' },
  { path: '/helper-account', public: false, access: 'helper', env: 'all' },

  // ── Admin (fine-grained sub-route access: since the #894 cutover the
  //    server's allowed_admin_routes() decides it, for both ProtectedAdminRoute
  //    in App.tsx and AdminSidebar.tsx's nav, keyed by ADMIN_ROUTE_KEYS in
  //    src/lib/adminRouteRoles.ts. This top-level registry still only tracks
  //    'admin' as a coarse access level.) ──
  { path: '/admin-utils', public: false, access: 'admin', env: 'all' },
  { path: '/admin/legacy-settings', public: false, access: 'admin', env: 'all' },
  { path: '/admin/approvals', public: false, access: 'admin', env: 'all', redirect: '/admin/approvals-hub' },
  { path: '/dashboard/admin', public: false, access: 'admin', env: 'all', redirect: '/admin' },
  { path: '/admin', public: false, access: 'admin', env: 'all' },
];

/** env:'dev' entries only exist (and are only public) in dev builds. */
function envActive(r: RouteMeta, isDev: boolean): boolean {
  return r.env !== 'dev' || isDev;
}

/** Public path prefixes derived from the registry for a given build env. */
export function computePublicPrefixes(registry: RouteMeta[], isDev: boolean): string[] {
  return Array.from(
    new Set(
      registry
        .filter((r) => r.public && envActive(r, isDev))
        .map((r) => staticPrefix(r.path)),
    ),
  );
}

const PREFIXES_DEV = computePublicPrefixes(ROUTE_REGISTRY, true);
const PREFIXES_PROD = computePublicPrefixes(ROUTE_REGISTRY, false);

/** Param-free registry paths, for router-style exact resolution: a static route
 *  (e.g. /helper/bookings) beats the prefix heuristic (/helper/:helperId → /helper),
 *  the same specificity rule React Router applies when matching <Route>s. */
const STATIC_ENTRIES = new Map<string, RouteMeta>(
  ROUTE_REGISTRY.filter((r) => !r.path.includes('/:')).map((r) => [r.path, r]),
);

/** Routes exempt from post-login redirect handling. */
export const AUTH_EXEMPT_PATHS: string[] = ROUTE_REGISTRY.filter((r) => r.authExempt).map(
  (r) => r.path,
);

/** Core public-route resolution; isDev is injectable so tests can cover the
 *  production build's behaviour (dev sandbox routes are not public there). */
export function resolveIsPublic(pathname: string, isDev: boolean): boolean {
  const exact = STATIC_ENTRIES.get(pathname);
  if (exact) return exact.public && envActive(exact, isDev);
  const prefixes = isDev ? PREFIXES_DEV : PREFIXES_PROD;
  return prefixes.some(
    (route) =>
      pathname === route ||
      pathname.startsWith(route + '/') ||
      pathname.startsWith(route + '?'),
  );
}

/**
 * Does this path correspond to ANY registered route?
 *
 * Params are matched segment-wise (/helper/:helperId matches /helper/abc), and
 * a registered path also covers everything nested beneath it, which is how the
 * relative admin sub-routes (/admin/customers …) resolve through their /admin
 * parent instead of looking unknown.
 */
export function isKnownRoute(pathname: string): boolean {
  const path = pathname.split('?')[0];
  return ROUTE_REGISTRY.some((r) => {
    if (r.path === path) return true;
    if (path.startsWith(r.path + '/')) return true;
    if (!r.path.includes('/:')) return false;
    const rx = new RegExp('^' + r.path.replace(/:[^/]+/g, '[^/]+') + '(/|$)');
    return rx.test(path);
  });
}

/**
 * AuthWrapper's public-route test, derived from the registry.
 *
 * An UNRECOGNISED path is treated as public so the wildcard <Route path="*">
 * can render NotFound. Previously anything unmatched counted as protected, so
 * AuthWrapper redirected first and a mistyped URL landed on "Welcome back!" —
 * a login screen instead of a 404, for signed-in and signed-out visitors
 * alike, which also made the NotFound page unreachable dead code.
 *
 * This grants no access: unknown paths have no data behind them, and every
 * REGISTERED protected route still resolves through the registry as before.
 */
export function isPublicPath(pathname: string): boolean {
  if (resolveIsPublic(pathname, import.meta.env.DEV)) return true;
  return !isKnownRoute(pathname);
}

/** AuthWrapper's auth-exempt test, derived from the registry. */
export function isAuthExemptPath(pathname: string): boolean {
  return AUTH_EXEMPT_PATHS.some(
    (route) => pathname === route || pathname.startsWith(route + '/'),
  );
}

export function userTypeMatches(
  requiredUserType: UserType | undefined,
  profile: {
    is_helper: boolean;
    role?: string;
    profile_status?: string | null;
    account_status?: string | null;
  },
): boolean {
  switch (requiredUserType) {
    case 'helper':
      return profile.is_helper === true
        && profile.profile_status === 'approved'
        && profile.account_status !== 'deactivated'
        && profile.account_status !== 'deleted';
    case 'client':
      return true; // additive model: any authenticated user may act as a client
    case 'admin':
      return profile.role === 'admin';
    default:
      return true;
  }
}
